import {observer} from 'mobx-react-lite';
import {ProjectDialogProps} from '../../../../entity/props/project/project-dialog-props';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';
import {useService} from '../../../../infrastructure/service/service';
import React, {useEffect, useState} from 'react';
import {State} from '../../../../entity/model/state';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import LoadingComponent from '../widget/LoadingComponent';
import {User} from '../../../../entity/model/user';
import moment from 'moment/moment';
import {SearchType} from '../../../../entity/enums/search-type';
import {County} from '../../../../entity/model/county';
import {ManageMode} from '../../../../entity/enums/manage-mode';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {ProjectInfoDto} from '../../../../entity/dto/project-info-dto';
import TableSortLabel from '@mui/material/TableSortLabel';
import {ParcelInfoDto} from '../../../../entity/dto/parcel-info-dto';
import Skeleton from 'react-loading-skeleton';
import AddIcon from '@mui/icons-material/Add';
import ParcelDialog from './ParcelDialog';
import {ProjectDto} from '../../../../entity/dto/project-dto';
import {toast} from 'react-toastify';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {TitleSectionTemplate} from '../../../../entity/model/title-section-template';
import {Organization} from '../../../../entity/model/organization';
import {DefaultOrderData} from '../../../../entity/resource/default-order-data';
import PlacesAutocomplete from 'react-google-places-autocomplete';

import '../../../../assets/css/style.css';
import '../../../../assets/css/project/order-dialog.css';

const ProjectDialog = (props: ProjectDialogProps): JSX.Element => {
  const {projectService, stateService, userService, countyService, titleSectionTemplateService, organizationService, titleService} = useService();

  const [counties, setCounties] = useState<County[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [userOptions, setUserOptions] = useState<DropdownOption[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [searchTypeOptions, setSearchTypeOptions] = useState<DropdownOption[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<User>();
  const [mode, setMode] = useState<ManageMode>(ManageMode.CREATE);
  const [openParcelDialog, setOpenParcelDialog] = useState<boolean>(false);
  const [parcelEntries, setParcelEntries] = useState<ParcelInfoDto[]>([]);
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [selectedParcelEntry, setSelectedParcelEntry] = useState<ParcelInfoDto>();
  const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
  const [countyOptions, setCountyOptions] = useState<DropdownOption[]>([]);
  const [sameOrganization, setSameOrganization] = useState<boolean>(false);
  const [createdOrganization, setCreatedOrganization] = useState<boolean>(false);
  const [titleSectionTemplateOptions, setTitleSectionTemplateOptions] = useState<DropdownOption[]>([]);
  const [organizationOptions, setOrganizationOptions] = useState<DropdownOption[]>([]);
  const [defaultOrderData, setDefaultOrderData] = useState<DefaultOrderData>();

  const tableHeaders = ['#', 'Parcel ID', 'Address', 'State', 'County', 'Abstractor', 'Reviewer', 'Approver'];

  useEffect(() => {
    const init = async (): Promise<void> => {
      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      const states = await stateService.getStates();
      setStates(states);
      setStateOptions(states.map((state: State) => ({key: state.id, text: state.shortName, value: state.id})));

      const users = await userService.getAllUsers(user?.organizationID ?? '', false);
      setUsers(users);
      setUserOptions(users.map((user: User) => ({key: user.id, text: userService.getDisplayName(false, user.id), value: user.id})));

      const searchTypes = Object.keys(SearchType).map((key: string) => ({key: key, text: SearchType[key as keyof typeof SearchType], value: key}));
      setSearchTypeOptions(searchTypes);

      const counties = await countyService.getCounties();
      setCounties(counties);

      if (props.mode === ManageMode.CREATE)
        setCountyOptions(counties.filter((county: County) => county.stateID === states[0].id).sort((a: County, b: County) => a.name.localeCompare(b.name)).map((county: County) => ({key: county.id, text: county.name, value: county.id})));
      if (props.mode === ManageMode.UPDATE && props.project) {
        let stateCounties = counties.filter((county: County) => county.stateID === props.project?.stateID).sort((a: County, b: County) => a.name.localeCompare(b.name));
        setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));

        setSameOrganization(user?.organizationID === props.project.assignedToOrganizationID);

        const entries = [];
        const titles = await titleService.getTitles(props.project.createdByOrganizationID, props.project.id);

        for (const title of titles) {
          const parcelEntry: ParcelInfoDto = {
            id: title.id,
            assignedToOrganizationID: title.assignedToOrganizationID,
            tmsParcelNumber: title.tmsParcelNumbers[0],
            tmsParcelNumbers: title.tmsParcelNumbers,
            assignedToUserID: title.assignedToUserID,
            assignedToReviewerUserID: title.assignedToReviewerUserID,
            assignedToApproverUserID: title.assignedToApproverUserID,
            address: title.address,
            stateID: title.stateID,
            countyID: title.countyID,
            createdByUserID: title.createdByUserID
          }

          entries.push(parcelEntry);
        }
        setParcelEntries(entries);
      }

      const titleSectionTemplates = await titleSectionTemplateService.getTitleSectionTemplates(user?.organizationID ?? '', false);

      let options: DropdownOption[] = titleSectionTemplates.map((titleSectionTemplate: TitleSectionTemplate) => ({key: titleSectionTemplate.id, text: titleSectionTemplate.name, value: titleSectionTemplate.id}));
      options.push({key: 'none', text: 'None', value: ''});

      setTitleSectionTemplateOptions(options);

      const organizations = await organizationService.getOrganizations(user?.organizationID);
      setOrganizationOptions(organizations.map((organization: Organization) => ({key: organization.id, text: organization.name, value: organization.id})));

      setDefaultOrderData({
        stateID: props.mode === ManageMode.CREATE ? '' : props.project?.stateID ?? '',
        countyID: props.mode === ManageMode.CREATE ? '' : props.project?.countyID ?? '',
        assignedToOrganizationID: props.mode === ManageMode.CREATE ? '' : props.project?.assignedToOrganizationID ?? '',
        assignedToUserID: props.mode === ManageMode.CREATE ? '' : props.project?.assignedToUserID ?? '',
        titleSectionTemplateID: props.mode === ManageMode.CREATE ? '' : props.project?.titleSectionTemplateID ?? ''
      });
      setCreatedOrganization(props.mode === ManageMode.CREATE ? true : props.project?.createdByOrganizationID === user?.organizationID);

      if (props.mode === ManageMode.UPDATE && props.project) {
        const originalValues: ProjectInfoDto = {
          orderNumber: props.project.orderNumber || '',
          dueDate: props.project.dueDate || '',
          searchType: props.project.searchType || '',
          stateID: props.project.stateID || '',
          countyID: props.project.countyID || '',
          assignedToOrganizationID: props.project.assignedToOrganizationID || '',
          assignedToUserID: props.project.assignedToUserID || '',
          assignedToApproverUserID: '',
          assignedToReviewerUserID: '',
          client: props.project.client || '',
          buyers: props.project.buyers || '',
          sellers: props.project.sellers || '',
          note: props.project.note || '',
          titleSectionTemplateID: props.project.titleSectionTemplateID || '',
          address: '',
          tmsParcelNumber: ''
        }
      }
    }

    init().then(() => setLoading(false));
  }, [props.project]);

  const handleValidate = (values: ProjectInfoDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!values.orderNumber)
      errors.orderNumber = 'File Name is required.';

    if (!values.dueDate)
      errors.dueDate = 'Due Date is required.';

    if (!values.searchType)
      errors.searchType = 'Search Type is required.';

    if (!values.stateID)
      errors.stateID = 'State is required.';

    if (!values.countyID)
      errors.countyID = 'County is required.';

    if (!values.assignedToOrganizationID)
      errors.assignedToOrganizationID = 'Abstractor Organization is required.';

    return errors;
  }

  const isFormValid = (values: ProjectInfoDto): boolean => {
    return !!(
      values.orderNumber &&
      values.dueDate &&
      values.searchType &&
      values.stateID &&
      values.countyID &&
      values.assignedToOrganizationID &&
      values.address &&
      values.tmsParcelNumber &&
      values.assignedToApproverUserID
    ) || parcelEntries.length > 0;
  }

  const handleSubmit = async (values: ProjectInfoDto, {setSubmitting}: FormikHelpers<ProjectInfoDto>): Promise<void> => {
    setSubmitting(true);

    let projectTitles: ParcelInfoDto[] = [];

    if (parcelEntries.length === 0) {
      if (values.address && values.tmsParcelNumber && values.assignedToOrganizationID) {

        projectTitles = [{
          address: values.address,
          tmsParcelNumber: values.tmsParcelNumber,
          tmsParcelNumbers: [values.tmsParcelNumber],
          stateID: values.stateID,
          countyID: values.countyID,
          assignedToUserID: values.assignedToUserID,
          assignedToReviewerUserID: values.assignedToReviewerUserID,
          assignedToApproverUserID: values.assignedToApproverUserID,
          assignedToOrganizationID: values.assignedToOrganizationID,
          titleSectionTemplateID: values.titleSectionTemplateID
        }]

      } else {
        toast.warning('Please add at least one parcel entry.')
        return;
      }
    } else {
      projectTitles = parcelEntries.map((parcelEntry: ParcelInfoDto): ParcelInfoDto => {
        const { id, createdByUserID, ...rest } = parcelEntry;

        return {
          ...(createdByUserID ? { id } : {}),
          ...rest,
          tmsParcelNumbers: Array.isArray(parcelEntry.tmsParcelNumbers)
            ? parcelEntry.tmsParcelNumbers
            : [parcelEntry.tmsParcelNumbers],
          titleSectionTemplateID: values.titleSectionTemplateID === '' ? parcelEntry.titleSectionTemplateID : values.titleSectionTemplateID
        }
      });
    }

    const sameOrg = values.assignedToOrganizationID === currentUser?.organizationID;

    const payload: ProjectDto = {
      orderNumber: values.orderNumber,
      assignedToOrganizationID: values.assignedToOrganizationID,
      assignedToUserID: sameOrg ? values.assignedToUserID : props.mode === ManageMode.UPDATE ? props.project?.assignedToUserID ?? '' : '',
      dueDate: moment(values.dueDate).format('YYYY-MM-DD'),
      searchType: values.searchType,
      stateID: values.stateID,
      countyID: values.countyID,
      titleSectionTemplateID: values.titleSectionTemplateID,
      buyers: values.buyers,
      sellers: values.sellers,
      client: values.client,
      note: values.note,
      projectTitles: projectTitles
    };

    if (props.mode === ManageMode.CREATE)
      await projectService.createProject(currentUser?.organizationID ?? '', payload);
    else
      await projectService.updateProject(props.project?.createdByOrganizationID ?? '', props.project?.id ?? '',  payload);

    props.setOpen(false);
  }

  const handleParcelSubmit = (parcelEntry: ParcelInfoDto): void => {
    parcelEntry = {...parcelEntry, tmsParcelNumbers: [parcelEntry.tmsParcelNumber ?? '']};

    setParcelEntries((prevState: ParcelInfoDto[]): ParcelInfoDto[] => {
      const exists = prevState.some((entry: ParcelInfoDto): boolean => entry.id === parcelEntry.id);

      if (exists) {
        setOpenParcelDialog(false);
        return prevState.map((entry: ParcelInfoDto): ParcelInfoDto => entry.id === parcelEntry.id ? parcelEntry : entry);
      } else {
        const tmsNumberExists = prevState.some((entry: ParcelInfoDto): boolean => entry.tmsParcelNumber === parcelEntry.tmsParcelNumber);
        if (tmsNumberExists) {
          toast.error(`TMS Number "${parcelEntry.tmsParcelNumber}" already exists. Please use a unique TMS number.`);
          return prevState;
        }

        setOpenParcelDialog(false);
        return [...prevState, parcelEntry];
      }
    });
  }

  const handleClearAllParcels = (): void => {
    setParcelEntries([]);
    toast.info('All parcels have been cleared.');
  }

  const handleViewParcelDialog = (mode: ManageMode): void => {
    setOpenParcelDialog(true);
    setMode(mode);

    if(mode === ManageMode.UPDATE)
      handleMenuClose();
  }

  const handleDeleteParcel = (): void => {
    setParcelEntries((prevState: ParcelInfoDto[]): ParcelInfoDto[] => {
      return prevState.filter((parcelEntry: ParcelInfoDto) => parcelEntry.tmsParcelNumber !== selectedParcelEntry?.tmsParcelNumber)
    });

    handleMenuClose();
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, parcelEntry: ParcelInfoDto): void => {
    setMenuElement(event.currentTarget);
    setSelectedParcelEntry(parcelEntry);
  }

  const handleMenuClose = (): void => {
    setMenuElement(null);
  }

  const handleStateChange = (handleChange: any) => (event: any) => {
    let stateCounties = counties.filter((county: County) => county.stateID === event.target.value).sort((a: County, b: County) => a.name.localeCompare(b.name));
    setCountyOptions(stateCounties.map((county: County) => ({key: county.id, text: county.name, value: county.id})));

    handleChange({target: {name: event.target.name, value: event.target.value}});

    setParcelEntries((prev) => (prev.length === 1 ? prev.map((entry, i) => (i === 0 ? {...entry, stateID: event.target.value} : entry)) : prev));

    setDefaultOrderData(prevState => {
      if (!prevState) return prevState;

      return {
        ...prevState,
        stateID: event.target.value
      }
    });
  }

  const handleCountyChange = (handleChange: any) => (event: any) => {
    handleChange({target: {name: event.target.name, value: event.target.value}});

    setParcelEntries((prev) => (prev.length === 1 ? prev.map((entry, i) => (i === 0 ? {...entry, countyID: event.target.value} : entry)) : prev));

    setDefaultOrderData(prevState => {
      if (!prevState) return prevState;

      return {
        ...prevState,
        countyID: event.target.value
      }
    });
  }

  const handleOrganizationChange = (event: React.SyntheticEvent<Element, Event>, newValue: DropdownOption | null, handleChange: any) => {
    handleChange({ target: { name: 'assignedToOrganizationID', value: newValue?.value || '' }});
    setSameOrganization(newValue?.value === currentUser?.organizationID);

    setDefaultOrderData(prevState => {
      if (!prevState) return prevState;

      return {
        ...prevState,
        assignedToOrganizationID: newValue?.value ?? ''
      }
    });
  }

  const handleAbstractorChange = (handleChange: any) => (event: any) => {
    handleChange({target: {name: event.target.name, value: event.target.value}});

    setDefaultOrderData(prevState => {
      if (!prevState) return prevState;

      return {
        ...prevState,
        assignedToUserID: event.target.value
      }
    });
  }

  const handleSectionTemplateChange = (handleChange: any) => (event: any) => {
    handleChange({target: {name: event.target.name, value: event.target.value}});

    setDefaultOrderData(prevState => {
      if (!prevState) return prevState;

      return {
        ...prevState,
        titleSectionTemplateID: event.target.value
      }
    });
  }

  const handleTmsChange = (handleChange: any) => (event: any) => {
    handleChange(event);

    const value = event.target?.value ?? '';
    setParcelEntries((prev) => (prev.length === 1 ? prev.map((entry, i) => (i === 0 ? {...entry, tmsParcelNumber: value, tmsParcelNumbers: [value]} : entry)) : prev));
  }

  const handleAddressChange = (setFieldValue: (field: string, value: string) => void) => (selected: { value: { description: string } } | null) => {
    const address = selected?.value?.description ?? '';
    setFieldValue('address', address);

    setParcelEntries((prev) => (prev.length === 1 ? prev.map((entry, i) => (i === 0 ? {...entry, address} : entry)) : prev));
  }

  const handleAddressInputChange = (setFieldValue: (field: string, value: string) => void) => (value: string) => {
    if (value !== '') {
      setFieldValue('address', value);

      setParcelEntries((prev) => (prev.length === 1 ? prev.map((entry, i) => (i === 0 ? {...entry, address: value} : entry)) : prev));
    }
  }

  const handleAddParcelPermission = (): boolean => {
    if (!currentUser)
      return false;

    if(props.mode === ManageMode.CREATE)
      return true;

    if (!props.project)
      return false;

    return props.project.createdByOrganizationID === currentUser.organizationID && projectService.isPermittedProject(props.project.id);
  }

  const handleDeleteParcelPermission = (): boolean => {
    if (props.mode === ManageMode.CREATE)
      return true;

    if (!selectedParcelEntry?.id)
      return true;

    if (!currentUser)
      return false;

    if (!selectedParcelEntry.createdByUserID)
      return true;

    return currentUser.id === selectedParcelEntry.createdByUserID;
  }

  const handleUpdateParcelPermission = (): boolean => {
    if (props.mode === ManageMode.CREATE)
      return true;

    if (!selectedParcelEntry?.id)
      return true;

    return projectService.isPermittedTitle(selectedParcelEntry.id);
  }

  const renderTableRow = (parcelEntry: ParcelInfoDto, index: number): JSX.Element => {
    return (
      <TableRow
        hover
        role='checkbox'
        tabIndex={-1}
        key={index}
        className={'table-cell'}>
        <TableCell className={'no-wrap'} padding='checkbox'>
          <IconButton
            id={`btn-parcel-menu-${index}`}
            className={'zitles-menu-trigger'}
            color='inherit'
            disableRipple
            disableFocusRipple
            onClick={(event: React.MouseEvent<HTMLElement>): void => handleMenuOpen(event, parcelEntry)}>
            <MoreHorizIcon />
          </IconButton>
          <Menu elevation={0} anchorEl={menuElement} open={Boolean(menuElement)} className={'zitles-menu'} onClose={handleMenuClose}>
            <MenuItem
              id={`menu-parcel-edit-${index}`}
              disableRipple
              disabled={!handleUpdateParcelPermission()}
              className={`zitles-menu-item ${!handleUpdateParcelPermission() ? 'disabled' : ''}`}
              onClick={() => handleViewParcelDialog(ManageMode.UPDATE)}>
              <EditIcon fontSize={'small'} sx={{marginLeft: '5px'}} className={'zitles-icon'}/>
              <Typography className={'zitles-menu-item button'}>Edit</Typography>
            </MenuItem>
            <MenuItem
              id={`menu-parcel-delete-${index}`}
              disableRipple
              disabled={!handleDeleteParcelPermission()}
              className={`zitles-menu-item ${!handleDeleteParcelPermission() ? 'disabled' : ''}`}
              onClick={handleDeleteParcel}>
              <DeleteIcon className={'zitles-icon'}/>
              <Typography className={'zitles-menu-item button'}>Delete</Typography>
            </MenuItem>
          </Menu>
        </TableCell>
        <TableCell id={`text-parcel-id-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{index+1}</TableCell>
        <TableCell id={`text-parcel-tms-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{parcelEntry.tmsParcelNumber}</TableCell>
        <TableCell id={`text-parcel-address-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{parcelEntry.address}</TableCell>
        <TableCell id={`text-parcel-state-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            states.length > 0
              ? parcelEntry.stateID === null ? '' : states.find((state: State): boolean => state.id === parcelEntry.stateID)?.shortName ?? ''
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-parcel-county-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            counties.length > 0
              ? parcelEntry.countyID === null ? '' : counties.find((county: County): boolean => county.id === parcelEntry.countyID)?.name ?? ''
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-parcel-abstractor-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            users.length > 0
              ? userService.getDisplayName(false, parcelEntry?.assignedToUserID)
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-parcel-reviewer-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            users.length > 0
              ? userService.getDisplayName(false, parcelEntry?.assignedToReviewerUserID)
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-parcel-approver-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            users.length > 0
              ? userService.getDisplayName(false, parcelEntry?.assignedToApproverUserID)
              : <Skeleton/>
          }
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {
        loading
          ? <LoadingComponent/>
          : <Dialog open={props.open} fullScreen>
              <DialogContent className={'order-dialog-content'}>
                <Typography className={'zitles-dialog-title'}>
                  <span>{props.mode?.toString() + 'File'}</span>
                </Typography>
                <Typography className={'order-dialog-subtitle'}>Enter details below for every parcel in the file.</Typography>
                <Typography className={'order-dialog-section'}>File Information</Typography>
                <Formik
                  initialValues={projectService.getProjectInfoDto(searchTypeOptions, props.mode, props.project)}
                  validate={handleValidate}
                  onSubmit={handleSubmit}>
                  {({values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue}: FormikProps<ProjectInfoDto>): JSX.Element => (
                    <Form onSubmit={handleSubmit}>
                      <Grid container spacing={2}>
                        <Grid size={props.mode === ManageMode.UPDATE ? 10 / 3 : 2.5}>
                          <TextField
                            id={'input-order-number'}
                            required
                            fullWidth
                            label='File Name'
                            name='orderNumber'
                            value={values.orderNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.orderNumber && Boolean(errors.orderNumber)}
                            helperText={touched.orderNumber && errors.orderNumber}/>
                        </Grid>
                        <Grid size={2}>
                          <TextField
                            id={'input-due-date'}
                            required
                            fullWidth
                            type={'date'}
                            label='Due Date'
                            name='dueDate'
                            onBlur={handleBlur}
                            value={moment(values.dueDate).format('YYYY-MM-DD')}
                            onChange={handleChange}
                            error={touched.dueDate && Boolean(errors.dueDate)}
                            helperText={touched.dueDate && errors.dueDate}/>
                        </Grid>
                        <Grid size={props.mode === ManageMode.UPDATE ? 10 / 3 : 2.5}>
                          <FormControl required fullWidth error={touched.searchType && Boolean(errors.searchType)}>
                            <InputLabel>Search Type</InputLabel>
                            <Select
                              id={'input-search-type'}
                              name='searchType'
                              value={values.searchType}
                              label='Search Type'
                              onBlur={handleBlur}
                              onChange={(event: SelectChangeEvent) => handleChange({target: {name: event.target.name, value: event.target.value}})}>
                              {
                                searchTypeOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                              }
                            </Select>
                            <FormHelperText>{touched.searchType && errors.searchType}</FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid size={props.mode === ManageMode.UPDATE ? 10 / 3 : 2.5}>
                          <TextField
                            id={'input-client'}
                            fullWidth
                            label='Client'
                            name='client'
                            value={values.client}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.client && Boolean(errors.client)}
                            helperText={touched.client && errors.client}/>
                        </Grid>
                        {
                          props.mode === ManageMode.CREATE &&
                            <Grid size={2.5}>
                              <FormControl fullWidth>
                                <InputLabel>Title Section Template</InputLabel>
                                <Select
                                  id={'input-title-section-template'}
                                  name="titleSectionTemplateID"
                                  value={values.titleSectionTemplateID}
                                  label="Title Section Template"
                                  onChange={handleSectionTemplateChange(handleChange)}>
                                  {titleSectionTemplateOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)}
                                </Select>
                              </FormControl>
                            </Grid>
                        }
                        <Grid size={3}>
                          <FormControl required fullWidth error={touched.stateID && Boolean(errors.stateID)}>
                            <InputLabel>State</InputLabel>
                            <Select
                              id={'input-state'}
                              name='stateID'
                              value={values.stateID}
                              label='State'
                              onBlur={handleBlur}
                              onChange={handleStateChange(handleChange)}>
                              {
                                stateOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                              }
                            </Select>
                            <FormHelperText>{touched.stateID && errors.stateID}</FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid size={3}>
                          <FormControl required fullWidth error={touched.countyID && Boolean(errors.countyID)}>
                            <InputLabel>County</InputLabel>
                            <Select
                              id={'input-county'}
                              name='countyID'
                              value={values.countyID}
                              label='County'
                              onBlur={handleBlur}
                              onChange={handleCountyChange(handleChange)}>
                              {
                                countyOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                              }
                            </Select>
                            <FormHelperText>{touched.countyID && errors.countyID}</FormHelperText>
                          </FormControl>
                        </Grid>
                        <Grid size={3}>
                          <PlacesAutocomplete
                            apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                            selectProps={{
                              inputId: 'input-address',
                              value: values.address ? { label: values.address, value: {description: values.address} } : null,
                              onChange: handleAddressChange(setFieldValue),
                              onInputChange: handleAddressInputChange(setFieldValue),
                              placeholder: ' Address'
                            }}/>
                          {
                            touched.address && errors.address &&
                              <Typography className='zitles-form-error'>{errors.address}</Typography>
                          }
                        </Grid>
                        <Grid size={3}>
                          <TextField
                            id={'input-tms'}
                            fullWidth
                            label='TMS/Parcel Number'
                            name='tmsParcelNumber'
                            value={values.tmsParcelNumber}
                            onChange={handleTmsChange(handleChange)}
                            onBlur={handleBlur}
                            error={touched.tmsParcelNumber && Boolean(errors.tmsParcelNumber)}
                            helperText={touched.tmsParcelNumber && errors.tmsParcelNumber}/>
                        </Grid>
                        <Grid size={6}>
                          <TextField
                            id={'input-buyer'}
                            fullWidth
                            label='Buyer(s) | Borrower(s)'
                            name='buyers'
                            value={values.buyers}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.buyers && Boolean(errors.buyers)}
                            helperText={touched.buyers && errors.buyers}/>
                        </Grid>
                        <Grid size={6}>
                          <TextField
                            id={'input-seller'}
                            fullWidth
                            label='Seller(s)'
                            name='sellers'
                            value={values.sellers}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            sx={{ zIndex: 0 }}
                            error={touched.sellers && Boolean(errors.sellers)}
                            helperText={touched.sellers && errors.sellers}/>
                        </Grid>
                        <Grid size={sameOrganization ? createdOrganization ? 3 : 4 : 6}>
                          <Autocomplete
                            options={organizationOptions}
                            getOptionLabel={(option: DropdownOption): string => option.text}
                            value={organizationOptions.find((option: DropdownOption): boolean => option.value === values.assignedToOrganizationID) || null}
                            onChange={(event: React.SyntheticEvent<Element, Event>, newValue: DropdownOption | null): void => handleOrganizationChange(event, newValue, handleChange)}
                            onBlur={handleBlur}
                            renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                              <TextField
                                {...params}
                                id={'input-assign-organization'}
                                name='assignedToOrganizationID'
                                label='Title Abstractor Organization'
                                error={touched.assignedToOrganizationID && Boolean(errors.assignedToOrganizationID)}
                                helperText={touched.assignedToOrganizationID && errors.assignedToOrganizationID}
                                required
                                fullWidth/>
                            )}/>
                        </Grid>
                        {
                          sameOrganization &&
                            <Grid size={createdOrganization ? 3 : 4}>
                              <FormControl fullWidth error={touched.assignedToUserID && Boolean(errors.assignedToUserID)}>
                                <InputLabel sx={{zIndex: 0}}>Abstractor</InputLabel>
                                <Select
                                  id={'input-abstractor'}
                                  name="assignedToUserID"
                                  value={values.assignedToUserID}
                                  label="Abstractor"
                                  onBlur={handleBlur}
                                  onChange={handleAbstractorChange(handleChange)}>
                                  {
                                    userOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                                  }
                                </Select>
                                <FormHelperText>{touched.assignedToUserID && errors.assignedToUserID}</FormHelperText>
                              </FormControl>
                            </Grid>
                        }
                        {
                          sameOrganization &&
                            <Grid size={createdOrganization ? 3 : 4}>
                              <FormControl fullWidth error={touched.assignedToReviewerUserID && Boolean(errors.assignedToReviewerUserID)}>
                                <InputLabel sx={{zIndex: 0}}>Reviewer</InputLabel>
                                <Select
                                  id={'input-reviewer'}
                                  name="assignedToReviewerUserID"
                                  value={values.assignedToReviewerUserID}
                                  label="Reviewer"
                                  onBlur={handleBlur}
                                  onChange={handleChange}>
                                  {
                                    userOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                                  }
                                </Select>
                                <FormHelperText>{touched.assignedToReviewerUserID && errors.assignedToReviewerUserID}</FormHelperText>
                              </FormControl>
                            </Grid>
                        }
                        {
                          createdOrganization &&
                            <Grid size={sameOrganization ? 3 : 6}>
                              <FormControl fullWidth error={touched.assignedToApproverUserID && Boolean(errors.assignedToApproverUserID)}>
                                <InputLabel sx={{zIndex: 0}}>Approver</InputLabel>
                                <Select
                                  id={'input-approver'}
                                  name="assignedToApproverUserID"
                                  value={values.assignedToApproverUserID}
                                  label="Final Approver"
                                  onBlur={handleBlur}
                                  onChange={handleChange}>
                                  {
                                    userOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                                  }
                                </Select>
                                <FormHelperText>{touched.assignedToApproverUserID && errors.assignedToApproverUserID}</FormHelperText>
                              </FormControl>
                            </Grid>
                        }
                        <Grid size={12}>
                          <TextField
                            id={'input-note'}
                            className={'zitles-textarea'}
                            multiline
                            rows={5}
                            fullWidth
                            label='Note'
                            name='note'
                            value={values.note}
                            onChange={handleChange}
                            error={touched.note && Boolean(errors.note)}
                            helperText={touched.note && errors.note}/>
                        </Grid>
                      </Grid>
                      <Box marginTop={4} marginBottom={2}>
                        <Typography className={'order-dialog-section'}>Parcel Entry</Typography>
                        <Paper className={'table-container'} elevation={0}>
                          <Box className={'toolbar-container'} display={'flex'} gap={1}>
                            {
                              handleAddParcelPermission() &&
                                <Button
                                  id={'btn-add-parcel'}
                                  className={`zitles-button ${values.address && values.tmsParcelNumber ? 'disable' : ''}`}
                                  variant="contained"
                                  disabled={!!(values.address && values.tmsParcelNumber)}
                                  startIcon={<AddIcon/>}
                                  onClick={(): void => handleViewParcelDialog(ManageMode.CREATE)}>
                                  Add Row
                                </Button>
                            }
                            {
                              handleDeleteParcelPermission() &&
                                <Button
                                  id={'btn-clear-parcels'}
                                  className={`zitles-button stats-toolbar-button refresh btn-clear ${values.address && values.tmsParcelNumber ? 'disable' : ''}`}
                                  disabled={!!(values.address && values.tmsParcelNumber)}
                                  onClick={handleClearAllParcels}>
                                  Clear All Rows
                                </Button>
                            }
                          </Box>
                          {
                            parcelEntries.length > 0
                              ? <TableContainer className={'zitles-table'} component={Paper}>
                                  <Table>
                                    <TableHead>
                                      <TableRow className={'table-header'}>
                                        <TableCell className={'no-wrap'} padding='checkbox'/>
                                        {
                                          tableHeaders.map((header: string): JSX.Element => (
                                            <TableCell key={header} className={'no-wrap'} align='left' padding='normal'>
                                              <TableSortLabel
                                                id={`column-parcel-${header}`}
                                                className={'table-header-text'}
                                                active={true}
                                                direction={'desc'}>
                                                {header}
                                              </TableSortLabel>
                                            </TableCell>
                                          ))
                                        }
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {
                                        parcelEntries.map(renderTableRow)
                                      }
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              : <Typography id={'text-empty-prompt-parcel-table'} className='empty-message'>No parcel entries found!</Typography>
                          }
                        </Paper>
                      </Box>
                      <Box className={'action-btn-container'}>
                        <Button
                          id={'btn-order-cancel'}
                          disableTouchRipple
                          onClick={(): void => props.setOpen(false)}
                          className={'zitles-button cancel'}>
                          Cancel
                        </Button>
                        <Button
                          id={'btn-order-submit'}
                          type='submit'
                          disabled={isSubmitting || !isFormValid(values)}
                          className={`zitles-button submit ${isSubmitting || !isFormValid(values) ? 'disable' : ''}`}
                          startIcon={isSubmitting ? <CircularProgress size={14} color='inherit' /> : null}>
                          {
                            isSubmitting ? props.mode === ManageMode.CREATE ? 'Creating File' : 'Updating File' : props.mode === ManageMode.CREATE ? 'Create File' : 'Update File'
                          }
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              </DialogContent>
            </Dialog>
      }
      {
        openParcelDialog &&
          <ParcelDialog
            open={openParcelDialog}
            setOpen={setOpenParcelDialog}
            mode={mode}
            states={states}
            counties={counties}
            usersOptions={userOptions}
            handleSubmit={handleParcelSubmit}
            createdOrganization={createdOrganization}
            parcelEntry={selectedParcelEntry}
            defaultData={defaultOrderData}
            existingParcelEntries={parcelEntries}/>
      }
    </>
  );
}

export default observer(ProjectDialog);
